import { Controller } from '@hotwired/stimulus';

export class QuickCheckController extends Controller {
    static values = { submitLoadingLabel: { type: String, default: 'Re-analysing…' } };
    static targets = [
        'question',
        'progressBar',
        'progressLabel',
        'backButton',
        'nextButton',
        'submitButton',
        'compiledDetails',
    ];

    declare readonly questionTargets: HTMLElement[];
    declare readonly progressBarTarget: HTMLElement;
    declare readonly progressLabelTarget: HTMLElement;
    declare readonly backButtonTarget: HTMLButtonElement;
    declare readonly nextButtonTarget: HTMLButtonElement;
    declare readonly submitButtonTarget: HTMLButtonElement;
    declare readonly compiledDetailsTarget: HTMLInputElement;
    declare readonly hasCompiledDetailsTarget: boolean;
    declare readonly submitLoadingLabelValue: string;

    private currentIndex = 0;

    connect(): void {
        this.showQuestion(0);
    }

    next(): void {
        if (!this.validateQuestion(this.currentIndex)) {
            return;
        }
        if (this.currentIndex < this.questionTargets.length - 1) {
            this.showQuestion(this.currentIndex + 1);
        }
    }

    previous(): void {
        if (this.currentIndex > 0) {
            this.showQuestion(this.currentIndex - 1);
        }
    }

    answerChanged(event: Event): void {
        const input = event.currentTarget as HTMLElement | null;
        const question = input?.closest<HTMLElement>('[data-quick-check-target="question"]');
        question?.querySelector<HTMLElement>('[data-quick-check-answer-error]')?.classList.add('hidden');
    }

    prepareSubmission(event: SubmitEvent): void {
        const firstUnansweredIndex = this.questionTargets.findIndex(
            (_question, index) => !this.validateQuestion(index),
        );
        if (firstUnansweredIndex >= 0) {
            event.preventDefault();
            this.showQuestion(firstUnansweredIndex);
            return;
        }

        const answeredQuestions = this.questionTargets.flatMap((questionElement) => {
            const question = questionElement.dataset.question?.trim() ?? '';
            const customAnswer = questionElement
                .querySelector<HTMLTextAreaElement>('[data-quick-check-custom-answer]')
                ?.value.trim() ?? '';
            const selectedAnswer = questionElement
                .querySelector<HTMLInputElement>('[data-quick-check-answer]:checked')
                ?.value.trim() ?? '';
            const answer = customAnswer || selectedAnswer;

            return question && answer ? [`${question} Answer: ${answer}.`] : [];
        });

        if (this.hasCompiledDetailsTarget) {
            this.compiledDetailsTarget.value = answeredQuestions.join(' ');
        }
        this.questionTargets.forEach((questionElement) => {
            const customAnswer = questionElement.querySelector<HTMLTextAreaElement>('[data-quick-check-custom-answer]')?.value.trim() ?? '';
            const selectedAnswer = questionElement.querySelector<HTMLInputElement>('[data-quick-check-answer]:checked')?.value.trim() ?? '';
            const answerValue = questionElement.querySelector<HTMLInputElement>('[data-quick-check-answer-value]');
            if (answerValue) answerValue.value = customAnswer || selectedAnswer;
        });
        this.submitButtonTarget.disabled = true;
        this.submitButtonTarget.textContent = this.submitLoadingLabelValue;
    }

    restart(): void {
        this.showQuestion(0);
    }

    private showQuestion(index: number): void {
        if (this.questionTargets.length === 0) {
            return;
        }

        this.currentIndex = Math.min(Math.max(index, 0), this.questionTargets.length - 1);
        this.questionTargets.forEach((question, questionIndex) => {
            question.classList.toggle('hidden', questionIndex !== this.currentIndex);
        });

        const isFirst = this.currentIndex === 0;
        const isLast = this.currentIndex === this.questionTargets.length - 1;
        this.backButtonTarget.classList.toggle('hidden', isFirst);
        this.nextButtonTarget.classList.toggle('hidden', isLast);
        this.submitButtonTarget.classList.toggle('hidden', !isLast);
        this.progressLabelTarget.textContent = `${this.currentIndex + 1} of ${this.questionTargets.length}`;
        this.progressBarTarget.style.width = `${((this.currentIndex + 1) / this.questionTargets.length) * 100}%`;
    }

    private validateQuestion(index: number): boolean {
        const question = this.questionTargets[index];
        if (!question) {
            return false;
        }

        const customAnswer = question
            .querySelector<HTMLTextAreaElement>('[data-quick-check-custom-answer]')
            ?.value.trim() ?? '';
        const selectedAnswer = question
            .querySelector<HTMLInputElement>('[data-quick-check-answer]:checked')
            ?.value.trim() ?? '';
        const isAnswered = Boolean(customAnswer || selectedAnswer);
        question
            .querySelector<HTMLElement>('[data-quick-check-answer-error]')
            ?.classList.toggle('hidden', isAnswered);
        return isAnswered;
    }
}
